import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UploadFile } from '@data/file/UploadFile';
import { UploadFileStatus } from '@data/file/UploadFileStatus';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { FileApiService } from '@shared/service/file-api.service';
import { FileUploadActions } from '@shared/store/file/file-upload.actions';
import { ViewUtil } from '@shared/util/view.util';
import { of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface FileUploadModel {
  uploadingFile: UploadFile;

  uploadedFileId: number;

  previewUrl: any;
}

@State<FileUploadModel>({
  name: 'file_upload'
})
@Injectable()
export class FileUploadState {
  @Selector([ FileUploadState ])
  static uploading(state: FileUploadModel): UploadFile {
    return state.uploadingFile;
  }

  constructor(private readonly fileApiService: FileApiService,
              private readonly viewUtil: ViewUtil) {
  }

  @Action(FileUploadActions.AddFile, { cancelUncompleted: true })
  async addFile(ctx: StateContext<FileUploadModel>, action: FileUploadActions.AddFile) {
    const { file } = action;

    ctx.patchState({ uploadingFile: { file, progress: 0, status: UploadFileStatus.NOT_STARTED } });
    ctx.dispatch(new FileUploadActions.StartUploading());
  }

  @Action(FileUploadActions.StartUploading, { cancelUncompleted: true })
  async startUploading(ctx: StateContext<FileUploadModel>, action: FileUploadActions.StartUploading) {
    ctx.dispatch(FileUploadActions.ResetState);
    const { uploadingFile } = ctx.getState();

    return this.fileApiService.upload(uploadingFile.file)
      .pipe(
        tap((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round(event.loaded / event.total * 100);

              ctx.patchState({ uploadingFile: { ...uploadingFile, status: UploadFileStatus.UPLOADING, progress } });
              break;
            case HttpEventType.Response:
              ctx.patchState({
                previewUrl: URL.createObjectURL(uploadingFile.file),
                uploadingFile: { ...uploadingFile, status: UploadFileStatus.DONE, progress: 100 },
                uploadedFileId: event.body.id
              });
              ctx.dispatch(new FileUploadActions.UploadingSuccess());
              break;
          }
        }),
        catchError(() => {
          ctx.patchState({ uploadingFile: { ...uploadingFile, status: UploadFileStatus.ERROR } });
          ctx.dispatch(new FileUploadActions.UploadingError());

          return of(null);
        })
      );
  }

  @Action(FileUploadActions.UploadingError, { cancelUncompleted: true })
  async uploadingError(ctx: StateContext<FileUploadModel>) {
    await this.viewUtil.confirmationModal({
      title: 'FILE_UPLOAD.UPLOADING_ERROR.TITLE',
      description: 'FILE_UPLOAD.UPLOADING_ERROR.DESCRIPTION',
      icon: 'x',
      iconClass: 'bg-red-900 text-red-500',
      confirmButtonText: 'FILE_UPLOAD.UPLOADING_ERROR.TRY_AGAIN'
    })
      .then(() => ctx.dispatch(new FileUploadActions.StartUploading()));
  }

  @Action(FileUploadActions.UploadingSuccess, { cancelUncompleted: true })
  async uploadingSuccess(ctx: StateContext<FileUploadModel>) {
    await this.viewUtil.confirmationModal({
      title: 'FILE_UPLOAD.UPLOADING_SUCCESS.TITLE',
      description: 'FILE_UPLOAD.UPLOADING_SUCCESS.DESCRIPTION',
      confirmButtonText: 'FILE_UPLOAD.UPLOADING_SUCCESS.OK',
      icon: 'file-type-jpg',
      iconClass: 'bg-green-900 text-green-500',
      showCancelButton: false
    });
  }

  @Action(FileUploadActions.SetInitialPreviewUrl, { cancelUncompleted: true })
  async setInitialPreviewUrl(ctx: StateContext<FileUploadModel>, action: FileUploadActions.SetInitialPreviewUrl) {
    ctx.patchState({ previewUrl: action.previewUrl, uploadedFileId: action.uploadedFileId });
  }


  @Action(FileUploadActions.ResetState, { cancelUncompleted: true })
  async resetState(ctx: StateContext<FileUploadModel>, action: FileUploadActions.ResetState) {
    ctx.patchState({ uploadingFile: undefined });
  }

  @Action(FileUploadActions.ResetPreview, { cancelUncompleted: true })
  async resetPreview(ctx: StateContext<FileUploadModel>, action: FileUploadActions.ResetPreview) {
    ctx.patchState({ previewUrl: undefined });
  }
}